/**
 * MaintenanceBanner — visar meddelande om planerat underhåll för inloggade.
 *
 * Backend äger tidslogiken helt: den här komponenten frågar bara
 * GET /maintenance/status och visar bannern när `bannerActive` är sann.
 * Ingen klocklogik i frontend — annars skulle användarens systemtid kunna
 * avgöra om bannern syns, vilket vore fel eftersom fönstret är definierat i
 * svensk tid på servern.
 *
 * Bannerfönstret och blockeringsfönstret är OBEROENDE: bannern visas ofta
 * långt innan importer faktiskt pausas, vilket är hela poängen.
 *
 * "Stäng" är klient-sidigt för sessionen, samma mönster som övriga
 * localStorage-flaggor (Settings-sidan). Nyckeln innehåller fönstrets
 * sluttid, så ett NYTT underhållsfönster visas igen även om användaren
 * stängt det förra.
 */
import React, { useEffect, useState } from "react";
import { Alert } from "antd";
import useMaintenanceStatus from "./useMaintenanceStatus";

const dismissKey = (status) => `maintenance_dismissed_${status.bannerEndLocal || "aktiv"}`;

const MaintenanceBanner = () => {
  const { status } = useMaintenanceStatus();
  const [dismissed, setDismissed] = useState(false);

  // Bannern kan vara stängd för sessionen. Nyckeln innehåller fönstrets
  // sluttid, så ett NYTT underhållsfönster visas igen även om det förra
  // stängdes. Läses om när status ändras, inte bara vid montering.
  useEffect(() => {
    if (!status) return;
    setDismissed(localStorage.getItem(dismissKey(status)) === "1");
  }, [status]);

  if (!status || !status.bannerActive || dismissed) {
    return null;
  }

  const onClose = () => {
    localStorage.setItem(dismissKey(status), "1");
    setDismissed(true);
  };

  // Blockeringen kan ligga i framtiden — visa när den slår till, så man
  // hinner starta sin import innan dess. Sluttiden fetstil: det är den enda
  // uppgiften läsaren faktiskt behöver kunna scanna av.
  // HELA intervallet visas, inte bara sluttiden. Med enbart "pausas till 23:00"
  // läser man lätt att pausen gäller NU — fast den kan börja först om flera
  // dagar, eftersom bannerfönstret är oberoende av blockeringsfönstret.
  const blockInfo =
    status.blockStartLocal && status.blockEndLocal ? (
      <>
        {status.blockActive ? "Nya importer är pausade " : "Nya importer pausas "}
        <strong>{status.blockStartLocal}</strong> till <strong>{status.blockEndLocal}</strong>.
      </>
    ) : null;

  // FÄRGVAL: amber ("warning") är GeoFix varumärkesfärg — knappar, menyval och
  // markeringar är gula. En amber banner smälter därför in i designen i stället
  // för att sticka ut, precis när den behöver märkas som mest. Därför "error"
  // (röd) när blockeringen faktiskt är aktiv, och "info" (blå) när den bara är
  // aviserad. Röd betyder här "något är avstängt", inte "något har kraschat".
  return (
    <Alert
      type={status.blockActive ? "error" : "info"}
      banner
      closable
      onClose={onClose}
      message={
        // ADMIN STYR TEXTEN. Komponenten lägger medvetet inte till någon egen
        // rubrik — tidigare fanns ett hårdkodat "Underhåll pågår."-prefix, men
        // det blev tautologiskt så fort admins eget meddelande sa samma sak.
        // Kvar läggs bara TIDSINTERVALLET, eftersom det är härlett ur fönstret
        // och därför alltid korrekt; skrev admin tiderna för hand skulle de
        // glida isär från de faktiska så fort fönstret ändrades.
        //
        // Mellanrummen skrivs som explicita {" "} — JSX trimmar bort
        // radledande blanksteg, så ett mellanslag inuti <> </> försvinner
        // tyst och meningarna klistras ihop ("pågår.Nya importer").
        <span>
          {status.message || "Planerat underhåll."}
          {blockInfo ? <>{" "}{blockInfo}</> : null}
        </span>
      }
    />
  );
};

export default MaintenanceBanner;
